import React, { useState, RefObject, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import {
  LandingBrandQuery,
  PashaItemsQuery,
  GetPashaFilterDataQuery
} from '../../../graphql/graphql';
import { TopProjectsBlock } from '../../PashaLanding/TopProjectsBlock';
import { ProjectsGrid } from '../../PashaLanding/ProjectsGridBlock';
import { AboutCompany } from '../../PashaLanding/AboutCompany';
import { SendRequestBlock } from '../../PashaLanding/SendRequestBlock';
import { AdvantagesBlock } from '../../PashaLanding/AdvantagesBlock';
import { NavBar } from '../../PashaLanding/NavBar';
import { useOnScreen } from '../../../hooks/useOnScreen';
import { BlackSection, MapContainerStub } from '../../PashaLanding/styled';
import { OffersSection } from '../../PashaLanding/OffersSection';
import { AdriverBannerBlock } from '../../PashaLanding/BannerBlock';
import { SortOption } from '../../../graphql/types';
import { PashaLandingSchema } from './PashaSchema';

const ObjectMapGoogleNoSSR = dynamic(
  () =>
    import('../../PashaLanding/PashaProjectsMap').then(m => m.ObjectMapGoogle),
  {
    ssr: false,
    loading: () => <MapContainerStub />
  }
);

export const PashaLanding: React.FC<{
  landingBrand?: LandingBrandQuery['landingBrand'];
  pashaItems?: PashaItemsQuery['items'];
  totalCount: number;
  sortOption: SortOption;
  currentFilterValues?: unknown;
  filtersParams?: GetPashaFilterDataQuery;
}> = ({
  currentFilterValues,
  filtersParams,
  landingBrand,
  pashaItems,
  totalCount,
  sortOption
}) => {
  const [projectsRef, setProjectseRef] =
    useState<RefObject<HTMLDivElement> | null>(null);
  const [locationRef, setLocationsRef] =
    useState<RefObject<HTMLDivElement> | null>(null);
  const [aboutRef, setAboutRef] = useState<RefObject<HTMLDivElement> | null>(
    null
  );
  const [advantagesRef, setadvantagesRef] =
    useState<RefObject<HTMLDivElement> | null>(null);
  const [pashaItemsRef, setPashaItemsRef] =
    useState<RefObject<HTMLDivElement> | null>(null);
  const [visibleTabRef, setVisibleTabRef] =
    useState<RefObject<HTMLDivElement> | null>(null);
  const mapBlockRef = useRef<HTMLDivElement | null>(null);
  const isMapVisible = useOnScreen(mapBlockRef, 400);
  const [mapWasVisible, setMapWasVisible] = useState(false);
  const [formRef, setFormRef] = useState<RefObject<HTMLDivElement> | null>(
    null
  );

  useEffect(() => {
    if (projectsRef) {
      setVisibleTabRef(projectsRef);
    }
  }, [projectsRef]);

  useEffect(() => {
    if (isMapVisible && !mapWasVisible) setMapWasVisible(true);
  }, [isMapVisible, mapWasVisible]);

  return (
    <>
      {landingBrand && <PashaLandingSchema landingBrand={landingBrand} />}
      <TopProjectsBlock landingBrand={landingBrand} />
      <ProjectsGrid
        landingBrand={landingBrand}
        setProjectseRef={setProjectseRef}
        setVisibleTabRef={setVisibleTabRef}
      />
      <AdriverBannerBlock banners={landingBrand?.banners} />

      <BlackSection>
        <div ref={mapBlockRef}>
          {isMapVisible || mapWasVisible ? (
            <ObjectMapGoogleNoSSR
              setLocationsRef={setLocationsRef}
              setVisibleTabRef={setVisibleTabRef}
              data={{
                placements: (landingBrand?.placements ?? []).map(p => ({
                  ...p,
                  url: p.url ?? '',
                  path: p.path ?? ''
                }))
              }}
            />
          ) : (
            <MapContainerStub />
          )}
        </div>
        <OffersSection
          pashaItems={pashaItems}
          totalCount={totalCount ?? 0}
          setVisibleTabRef={setVisibleTabRef}
          setPashaItemsRef={setPashaItemsRef}
          sortOption={sortOption}
          currentFilterValues={currentFilterValues}
          filtersParams={filtersParams}
        />
        <AdvantagesBlock
          placements={landingBrand?.placements ?? []}
          setadvantagesRef={setadvantagesRef}
          setVisibleTabRef={setVisibleTabRef}
        />
      </BlackSection>
      <AboutCompany
        setAboutRef={setAboutRef}
        setVisibleTabRef={setVisibleTabRef}
      />
      <NavBar
        {...{
          visibleTabRef,
          projectsRef,
          locationRef,
          aboutRef,
          advantagesRef,
          pashaItemsRef,
          formRef,
          fallbackMapRef: mapBlockRef
        }}
      />

      <SendRequestBlock
        setFormRef={setFormRef}
        setVisibleTabRef={setVisibleTabRef}
      />
    </>
  );
};
